import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import DoDisturbOnOutlinedIcon from '@mui/icons-material/DoDisturbOnOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { IOrganization, IPage } from 'common/src/types/interfaces';

import {
  getOrganizations,
  updateOrganization,
} from 'portal/src/api/OrganizationsService';
import { NEW_ID } from 'portal/src/constants/ReservedIDs';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import useAuth from '../../auth/useAuth';
import { dateFormatter } from '../administration/Form/Budgets/common';
import ConfirmDialog from '../common/ConfirmDialog';
import type { DeactivateConfig } from '../common/CustomToolbar';
import DataTable from '../common/DataTable';
import addBooleanColumnProps from '../common/tables/addBooleanColumnProps';
import ConfirmChangeActiveDialog from '../common/tables/ConfirmChangeActiveDialog';

const Organizations = () => {
  const { getUser } = useAuth();
  const { organizationId } = getUser();
  const navigate = useNavigate();
  const snackbarHandler = useSnackbarHandler();

  const {
    data: organizations,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const response = await getOrganizations();
      return response;
    },
    initialData: { data: [] } as IPage<IOrganization>,
  });

  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [showDeactivatedOrgs, setShowDeactivatedOrgs] = useState(false);
  const [selectedOrganization, setSelectedOrganization] =
    useState<IOrganization>(undefined);

  const { mutate: updateOrgStatus, isPending } = useMutation({
    mutationFn: async (organization: IOrganization) => {
      await updateOrganization(organization.id, {
        isActive: !organization.isActive,
      });
      await refetch();
      return organization;
    },
    onSuccess: (organization) => {
      snackbarHandler.success(
        organization.isActive
          ? MESSAGES.ORGANIZATION.DEACTIVATED
          : MESSAGES.ORGANIZATION.REACTIVATED,
      );
      setSelectedOrganization(undefined);
    },
    onError: () => {
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    },
  });

  const deactivateConfig: DeactivateConfig = {
    isChecked: showDeactivatedOrgs,
    onToggleCheckbox: setShowDeactivatedOrgs,
  };

  const gridColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 2 },
    { field: 'id', headerName: 'Organization ID', flex: 1 },
    {
      field: 'fiscalYearStartMonth',
      headerName: 'Fiscal Year Start Month',
      flex: 1,
      valueFormatter: (value) => {
        const date = new Date();
        date.setMonth(value);
        return dateFormatter(date, { month: 'long' });
      },
    },
    {
      field: 'isActive',
      headerName: 'Deactivated',
      width: 115,
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: false, yesIsGreen: false },
      }),
    },
  ];

  const handleNavigate = (id: string) => navigate(`./${id}`);

  const handleStatusActionClick = (organization: IOrganization) => {
    if (organizationId === organization.id && organization.isActive) {
      setOpenWarningDialog(true);
      return;
    }

    setSelectedOrganization(organization);
  };

  return (
    <>
      <DataTable
        actionsConfig={{
          extraActions: (row) => [
            {
              icon: row.isActive ? (
                <DoDisturbOnOutlinedIcon />
              ) : (
                <AddCircleOutlineIcon />
              ),
              label: row.isActive ? 'Deactivate' : 'Reactivate',
              onClick: () => handleStatusActionClick(row),
            },
          ],
          readActionConfig: {
            onClick: (id) => handleNavigate(id),
          },
        }}
        columns={gridColumns}
        contextName="organizations"
        dataFetchError={isError}
        loading={isFetching}
        rows={
          showDeactivatedOrgs
            ? organizations.data
            : organizations.data.filter(({ isActive }) => isActive)
        }
        toolbarSlotProps={{
          deactivateConfig,
          onNewButtonClick: () => handleNavigate(NEW_ID),
          showFilters: true,
        }}
      />
      <ConfirmDialog
        open={openWarningDialog}
        onClose={() => setOpenWarningDialog(false)}
        message="Please switch organizations before deactivating the current one."
        primaryButtonLabel="close"
        onPrimaryButtonClicked={() => setOpenWarningDialog(false)}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
      />
      <ConfirmChangeActiveDialog
        showDialog={!!selectedOrganization}
        isActive={selectedOrganization?.isActive}
        itemName="organization"
        onClose={() => setSelectedOrganization(undefined)}
        onConfirm={async () => await updateOrgStatus(selectedOrganization)}
        isLoading={isPending}
      />
    </>
  );
};

export default Organizations;
